"use client";

import React from "react";
import { ArrowRight, Users } from "lucide-react";
import { z } from "zod";
import Link from "next/link";

const TrustIndicatorSchema = z.object({
	icon: z.any().nullable().optional(),
	text: z.string(),
	prefix: z.string().optional(),
});

const ButtonSchema = z.object({
	text: z.string(),
	icon: z.any(),
});

const HeadingSchema = z.object({
	main: z.string(),
	highlight: z.string(),
	suffix: z.string(),
});

const HeroSchema = z.object({
	heading: HeadingSchema.default({
		main: "Find, Hire & Collaborate with",
		highlight: "World-Class BIM Vendors",
		suffix: "in One Place",
	}),
	description: z
		.string()
		.default(
			"PropTechBuzz connects you to 200+ pre-vetted BIM experts ready to bid on your project—while you learn, share and grow with a global architect community."
		),
	primaryButton: ButtonSchema.default({
		text: "Post Your BIM Project",
		icon: ArrowRight,
	}),
	secondaryButton: ButtonSchema.default({
		text: "Register BIM Vendor",
		icon: ArrowRight,
	}),
	trustIndicators: z.array(TrustIndicatorSchema).default([
		{
			icon: Users,
			text: "5,000+ Architects Trust Us",
		},
		{
			icon: null,
			text: "4.9/5 Average Rating",
			prefix: "★★★★★",
		},
		{
			icon: null,
			text: "On-Time Delivery",
			prefix: "98%",
		},
	]),
});

type HeroProps = z.input<typeof HeroSchema>;

const Hero: React.FC<HeroProps> = (props = {}) => {
	const data = HeroSchema.parse(props);

	return (
		<section className="relative left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] flex min-h-screen w-screen items-center justify-center overflow-hidden bg-gradient-to-br from-primary-600 via-primary-500 to-secondary-500">
			{/* Animated Background Pattern */}
			<div className="bg-grid-pattern absolute inset-0 opacity-10"></div>

			{/* 3D Wireframe Animation */}
			<div className="absolute inset-0 flex items-center justify-center">
				<div className="relative h-96 w-96 opacity-20">
					<div className="absolute inset-0 animate-pulse-slow rounded-lg border-2 border-white"></div>
					<div
						className="absolute inset-4 animate-float rounded-lg border border-white"
						style={{ animationDelay: "1s" }}
					></div>
					<div
						className="absolute inset-8 animate-pulse-slow rounded-lg border border-white"
						style={{ animationDelay: "2s" }}
					></div>
					<div
						className="absolute inset-12 animate-float rounded-lg border border-white"
						style={{ animationDelay: "3s" }}
					></div>
				</div>
			</div>

			{/* Content Container - Centered */}
			<div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="mx-auto max-w-4xl text-center">
					<h1 className="mb-6 animate-fade-in text-4xl font-bold leading-tight text-white md:text-6xl">
						{data.heading.main} <span className="text-yellow-300">{data.heading.highlight}</span>{" "}
						{data.heading.suffix}
					</h1>

					<p className="mb-8 animate-slide-up text-xl leading-relaxed text-blue-100 md:text-2xl">
						{data.description}
					</p>

					<div
						className="mb-12 flex animate-slide-up flex-col items-center justify-center gap-4 sm:flex-row"
						style={{ animationDelay: "0.2s" }}
					>
						<Link
							href={`https://docs.google.com/forms/d/e/1FAIpQLSc5lB0b3WS98CykKg-hTFDiTARIdZj-ygaS6GSBTjhljBcyqw/viewform`}
							target="_blank"
						>
							<button className="flex transform items-center space-x-2 rounded-lg bg-accent-500 px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all hover:scale-105 hover:bg-accent-600">
								<span>{data.primaryButton.text}</span>
								<data.primaryButton.icon className="h-5 w-5" />
							</button>
						</Link>
						<Link
							href="https://forms.gle/WQW3WCbSvke2SWU17"
							target="_blank"
							className="flex transform items-center space-x-2 rounded-lg bg-amber-500 px-8 py-4 text-lg font-semibold text-white transition-all hover:scale-105 hover:bg-amber-600"
						>
							<span>{data.secondaryButton.text}</span>
							<data.secondaryButton.icon className="h-5 w-5" />
						</Link>
					</div>

					{/* Trust Indicators */}
					<div
						className="flex animate-fade-in flex-col items-center justify-center space-y-2 text-blue-100 sm:flex-row sm:space-x-8 sm:space-y-0"
						style={{ animationDelay: "0.4s" }}
					>
						{data.trustIndicators.map((indicator, index) => (
							<div key={index} className="flex items-center space-x-2">
								{indicator.icon && <indicator.icon className="h-5 w-5" />}
								{indicator.prefix && (
									<span
										className={indicator.prefix.includes("★") ? "text-yellow-300" : "font-semibold"}
									>
										{indicator.prefix}
									</span>
								)}
								<span>{indicator.text}</span>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
};

export default Hero;
